'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import debounce from 'lodash.debounce'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import qs from 'qs'

import { useFilterChange } from 'modules/products'
import { RadioItem } from 'shared/ui/RadioGroup'
import { formatToSimpleDate } from 'shared/utils/date'

import { createQueryString, FILTER_NAMES } from '../utils'

import { ClearButton } from './ClearButton'
import HorizontalExpendablePanel from './HorizontalExpendablePanel'
import { FiltersRadioGroup } from './styles'

import styles from './WeeklyAvailabilityFilter.module.scss'

export interface DayInfo {
  date: string
  dayOfWeek: string
}

export const getDaysToFilterBy = (dates: string[]): DayInfo[] => {
  if (dates) {
    return dates.map(date => {
      const currentDate = new Date(date)

      return {
        date,
        dayOfWeek: currentDate.toLocaleDateString('pl-PL', { weekday: 'long' })
      }
    })
  } else {
    return []
  }
}

interface WeeklyAvailabilityFilterProps {
  deliveryDates: string[]
  isActive?: boolean
}
const WeeklyAvailabilityFilter = ({
  deliveryDates,
  isActive
}: WeeklyAvailabilityFilterProps) => {
  const [value, setValue] = useState<string | null>(null)
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const { setLastFilterChange } = useFilterChange()

  const daysToFilterBy = getDaysToFilterBy(deliveryDates)

  const handleFilterChange = useCallback(
    (filterValue: string | null) => {
      setValue(filterValue ?? 'all')
      setLastFilterChange(new Date())
      const newQueryString = createQueryString(
        new URLSearchParams(searchParams || ''),
        'daily_availability_plan',
        filterValue
      )

      router.replace(`${pathname}?${newQueryString}`)
    },
    [searchParams, pathname, router, setLastFilterChange]
  )

  useEffect(() => {
    if (!searchParams) return
    const parsed = qs.parse(searchParams.toString())
    const queryValue =
      (parsed.filter as { daily_availability_plan?: string })
        ?.daily_availability_plan || null

    const normalizedValue = queryValue ?? 'all'

    if (value !== normalizedValue) {
      setValue(normalizedValue)
    }
  }, [searchParams, value])

  const handleRadioChange = useMemo(
    () =>
      debounce((radioValue: string) => {
        handleFilterChange(radioValue === 'all' ? null : radioValue)
      }, 300),
    [handleFilterChange]
  )

  useEffect(() => {
    return () => {
      handleRadioChange.cancel()
    }
  }, [handleRadioChange])

  return (
    <HorizontalExpendablePanel
      isActive={isActive}
      title={FILTER_NAMES.Availability}
    >
      {isActive && (
        <ClearButton onClick={() => handleFilterChange(null)}>
          Wyczyść filtr
        </ClearButton>
      )}
      <FiltersRadioGroup
        name='desktop-availability'
        selectedValue={value}
        onChange={handleRadioChange}
      >
        <RadioItem value='all'>Dowolny dzień</RadioItem>
        {daysToFilterBy.map(day => (
          <RadioItem key={day.date} value={day.date}>
            <div className={styles.filterLabel}>
              <div>{day.dayOfWeek}</div>
              <div>{formatToSimpleDate(day.date)}</div>
            </div>
          </RadioItem>
        ))}
      </FiltersRadioGroup>
    </HorizontalExpendablePanel>
  )
}

export default WeeklyAvailabilityFilter
